/**
 * Cart state management — localStorage-backed with React Context.
 * Works for both guests and members.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "dcc_shop_cart";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function saveCart(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadCart());

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((product, variation, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.variationId === variation.id
      );
      if (existing) {
        return prev.map((i) =>
          i.variationId === variation.id
            ? { ...i, quantity: Math.min(i.quantity + qty, 99) }
            : i
        );
      }
      return [
        ...prev,
        {
          productId: product.id || product.square_id,
          productName: product.name,
          variationId: variation.id,
          variationName: variation.name,
          priceCents: variation.price_cents,
          currency: variation.currency || "AUD",
          imageUrl: (product.image_urls || [])[0] || "",
          quantity: qty,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((variationId, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.variationId !== variationId));
    } else {
      setItems((prev) =>
        prev.map((i) =>
          i.variationId === variationId
            ? { ...i, quantity: Math.min(qty, 99) }
            : i
        )
      );
    }
  }, []);

  const removeItem = useCallback((variationId) => {
    setItems((prev) => prev.filter((i) => i.variationId !== variationId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const totalCents = items.reduce((s, i) => s + i.priceCents * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQuantity, removeItem, clearCart, totalItems, totalCents }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}

export function formatPrice(cents, currency = "AUD") {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
